/**
 * utilGIS.js
 */

import Graphic from "@arcgis/core/Graphic.js";
//import Point from "@arcgis/core/geometry/Point.js";
// SimpleMarkerSymbol import not needed, but kept for completeness
//import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol.js";
import Multipoint from "@arcgis/core/geometry/Multipoint.js";
import Extent from "@arcgis/core/geometry/Extent.js";
import { defaultPinSymbol,selectedPinSymbol } from  '@libs/utils/mapUtils.js';
// --- UTILITY FUNCTIONS ---
/**
 * Iterates over all existing Graphics and updates their symbol based on selection status.
 * This should be called inside a useEffect block in your component.
 * * @param {Graphic[]} allGraphics The current array of Graphic objects on the map.
 * @param {string[]} selectedReservationIds An array of IDs for selected reservations.
 */
export const updateSelectedGraphicsSymbols = (allGraphics, selectedReservations) => {
   // console.log("updateSelectedGraphicsSymbols", selectedReservationIds);
    if (!allGraphics || allGraphics.length === 0) {
        return;
    }
    let selectedReservationIds = [];

    if (selectedReservations && selectedReservations.length > 0) {
        const firstElement = selectedReservations[0];

        // Check if the input is an array of objects
        if (
            typeof firstElement === 'object' && 
            firstElement !== null && 
            'id' in firstElement
        ) {
            // It's an array of objects, so map to get the IDs
            selectedReservationIds = selectedReservations.map(res => res.id);
        } else {
            // Assume it's already an array of IDs (strings or numbers)
            selectedReservationIds = selectedReservations;
        }
    }

    // 2. Create a Set from the IDs for O(1) lookups.
    const selectedSet = new Set(selectedReservationIds);
 
   allGraphics.forEach(graphic => {
        const resId = graphic.attributes?.id; 
        //console.log("graphic.attributes?.id",graphic.attributes?.id);
        if (!resId) return;
        
        const isSelected = selectedSet.has(resId); //console.log(isSelected)
        if (isSelected) {
                graphic.symbol = selectedPinSymbol; //console.log("swap to selected");
        } else {
               graphic.symbol = defaultPinSymbol;//console.log("swap to default");
        }
    });
};

export const zoomToPoint=({mapview,loc,zoom})=>{
    //console.log("Loc",loc);
    let props={target:loc};
    if(zoom) props={...props, zoom};
     mapview.goTo(props).catch(error => {
                    console.error('Error zooming to reservation:', error);
                });

}

const isValidLocation = (loc) => (
  loc && typeof loc.lng === "number" && typeof loc.lat === "number" &&
  !isNaN(loc.lng) && !isNaN(loc.lat)
);

/**
 * Calculates and returns the Extent (bounding box) for a list of selected reservations.
 * Handles single points by creating a small buffer extent.
 * @param {Array<string>} selectedReservationIds 
 * @param {Array<object>} allReservations 
 * @returns {Extent | null} The calculated Extent or null if no valid locations.
 */
export const getPoints = (selectedReservationIds, allReservations) => {
   const validPoints = selectedReservationIds
    .map(id => allReservations.find(res => res.id === id))
    .filter(Boolean)
    .filter(res =>isValidLocation(res?.location))
    .map(res => [res.location.lng, res.location.lat]);

    if (validPoints.length === 0) {
        return null;
    }
      
    // 3. Extract the extent (bounding box).
    return validPoints
};



/**
 * Calculates and returns the Extent (bounding box) for a list of selected reservations.
 * Handles single points by creating a small buffer extent.
 * @param {Array<string>} selectedReservationIds 
 * @param {Array<object>} allReservations 
 * @returns {Extent | null} The calculated Extent or null if no valid locations.
 */
export const getPointsExtentByMP = (selectedReservationIds, allReservations) => { console.log("getPointsExtentByMP::");
    const validPoints=getPoints(selectedReservationIds, allReservations);
    if (!validPoints ) { return null;  }

    if (validPoints.length === 1) {
        const [x, y] = validPoints[0];
        const buffer = 0.005;
        return new Extent({
        xmin: x - buffer,
        ymin: y - buffer,
        xmax: x + buffer,
        ymax: y + buffer,
        spatialReference: { wkid: 4326 }
        });
    }

    const multipoint = new Multipoint({
        points: validPoints, // ✅ correct format
        spatialReference: { wkid: 4326 }
    });

  return multipoint.extent; //.expand(-2);
};

// ReservationUtilGIS.js

/**
 * Attaches a click event handler to the mapView for graphic hit-testing.
 * @param {object} mapView - The ArcGIS JS API MapView instance.
 * @param {object} graphicLayerRef - A React ref object pointing to the GraphicsLayer.
 * @param {function} handleListItemSelect - The function to call when a graphic is clicked.
 * @returns {object} The event handler object from mapView.on().
 */
export const setupMapClickHandler = (mapView, graphicLayerRef, handleListItemSelect) => {
    // Safety check, though the caller's useEffect should handle it
    if (!mapView || !graphicLayerRef.current) {
        console.warn("setupMapClickHandler called without necessary objects.");
        return null;
    }

    const clickHandler = mapView.on("click", (event) => {
        mapView.hitTest(event).then((response) => {
            // Find the graphic hit from the correct GraphicsLayer
            const graphicHit = response.results.find(
                // Use graphicLayerRef.current.id inside the utility function
                (result) => result.graphic.layer?.id === graphicLayerRef.current.id
            );

            if (graphicHit) {
                const resId = graphicHit.graphic.attributes?.id;
                if (resId) {
                    // Call the passed-in callback function
                    handleListItemSelect(resId);
                }
            }
        }).catch(error => {
            console.error("Hit test failed:", error);
        });
    });

    return clickHandler; // Important: return the handler for cleanup
};